/*
  TagsField.jsx
*/

// Npm.
import React, { useMemo, useState } from 'react';

// Utils.
import cleanWhiteSpace from '../../../../utils/cleanWhiteSpace';

// Components.
import AutocompleteString from '../../../AutocompleteString';

// Makes.
import { makeStyles, cx } from '../../../../makes/makeStyles';

// Constants.
const NOOP = () => {};

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    '& > .tagsContainer': {
      display: 'flex',
      flexWrap: 'wrap',
      margin: theme.space(0, 0, 0, -0.5),
      '& > button': {
        margin: theme.space(0.5, 0, 0, 0.5),
        padding: theme.space(0.5, 1),
        color: theme.color_text_primary,
        display: 'inline-block',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '32px',
        backgroundColor: theme.color_divider,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        outline: 'none',
        lineHeight: '1',
        fontSize: 'inherit',
        '&:focus': {
          boxShadow: `0 0 0 1px ${theme.color_primary_main} inset`,
        },
        '&:hover': {
          backgroundColor: theme.action_hover,
        },
        '&:disabled': {
          cursor: 'inherit',
          color: theme.color_text_disabled,
          backgroundColor: theme.action_hover,
        },
      },
    },
  },
  noneText: {
    color: theme.color_text_disabled,
    fontStyle: 'italic',
  },
}));

/**
 * Field for editing array of tags.
 */
export default function TagsField({
  className,
  nodes,
  value,
  disabled = false,
  readOnly = false,
  onChange,
}) {

  // Get all options from the current list of nodes, minus the selected values.

  const options = useMemo(() => {
    const optionSet = new Set();
    if (nodes) {
      for (const node of nodes) {
        if (node._botech?.tags) {
          for (const tag of node._botech.tags) {
            optionSet.add(tag);
          }
        }
      }
    }
    if (value) {
      for (const val of value) {
        optionSet.delete(val);
      }
    }
    return [...optionSet];
  }, [value, nodes]);

  const useValue = value || [];

  // Keep a local value for the autocomplete input form.
  // Whenever this changes, set to empty and append.

  const [inputValue, setInputValue] = useState('');

  function handleChangeInputValue(val, type) {
    setInputValue('');
    val = cleanWhiteSpace(val);
    if (val && onChange && type !== 'blur' && !useValue.includes(val)) {
      onChange(
        useValue
          .concat(val)
          .sort((a, b) => a.localeCompare(b))  // Sort alphabetically.
        );
    }
  }

  // Helper to remove a value.

  function removeValue(val) {
    if (onChange && useValue.includes(val)) {
      onChange(useValue.filter(v => v !== val));
    }
  }

  // Classes.

  const classes = useStyles();
  const cls = useMemo(() => cx('TagsField', classes.root, className), [classes.root, className]);

  // TODO: Cannot tab out of AutocompleteString properly
  // when the dropdown is open.

  return (
    <div className={cls}>

      {
        readOnly ? (
          <div className="caption">
            { useValue.length > 0 ? `Tags (${useValue.length})` : 'Tags' }
          </div>
        ) : (
          <AutocompleteString
            label={useValue.length > 0 ? `Tags (${useValue.length})` : 'Tags'}
            placeholder="Type and hit Enter"
            value={inputValue}
            options={options}
            onChange={handleChangeInputValue}
            disabled={disabled}
            disableCloseOnSelect
            fullWidth
          />
        )
      }

      {
        useValue.length === 0 && readOnly &&
        <div className={classes.noneText}>
          (none)
        </div>
      }

      {
        useValue.length > 0 &&
        <div className="tagsContainer caption">
          {
            useValue.map(val => {

              const handleClick = readOnly ? NOOP : (event) => {
                event.preventDefault();
                removeValue(val);
              };

              const handleKeyDown = readOnly ? readOnly : (event) => {
                if (event.key === 'Backspace') {
                  event.preventDefault();
                  removeValue(val);
                }
              };

              return (
                <button
                  key={val}
                  title={val}
                  type="button"
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  disabled={disabled || readOnly}
                >
                  { val }
                </button>
              );
            })
          }
        </div>
      }

    </div>
  );
}
